import React from 'react';
import cloneDeep from 'lodash.clonedeep';
import { Label } from '@blueprintjs/core';
import i18n from 'i18n';
import setAllTemplateFieldsAsReadOnly from 'utils/formTemplate/setAllTemplateFieldsAsReadOnly';
import { FormTemplateFieldMap } from 'data/template/template.types';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import { KeyValuePair } from 'data/types';
import { getSortedFormTemplateLayout, TARGETS_DEFAULT_LAYOUT_ORDER } from 'utils/formTemplate/getSortedFormTemplateLayout';
import DynamicForm, { ChangedField } from '../../../shared/form';

export default class TargetDetail extends React.Component<any, any> {
  formRef: any;

  constructor(props: any) {
    super(props);
    this.state = {};
  }

  static getDerivedStateFromProps(props: any, state: any) {
    if (!props.fields) {
      return state;
    }

    // cloneDeep the fields into state only when 'required' atrribute changed.
    if (props.fields && state.fields) {
      const propsFieldRequired = props.fields.primaryValue.validation.required;
      const stateFieldRequired = state.fields.primaryValue.validation.required;
      if (propsFieldRequired === stateFieldRequired) {
        return state;
      }
    }
    const fields = cloneDeep(props.fields);
    const keys = Object.keys(fields);
    if (keys.length === 1) {
      fields[keys[0]].label = undefined;
    }
    state = {
      fields,
      target: props.target,
      targetConfirmation: props.targetConfirmation,
    };
    return state;
  }

  isValid = () => {
    return this.formRef.isValid(true, true) && this.isTargetConfirmationValidValid();
  };

  isTargetConfirmationValidValid = () => {
    const { isTargetIdValidationEnabled, isExtendedTargetValidationEnabled } = this.props;
    const { target, targetConfirmation } = this.state;

    if (isTargetIdValidationEnabled === false || isExtendedTargetValidationEnabled) {
      return true;
    }

    return target?.primaryValue === targetConfirmation?.primaryValue;
  };

  customModifyTargetInputByType = (trgt: any) => {
    const target = cloneDeep(trgt);

    if (target?.primaryType === 'VZID') {
      target.optionValue = target.optionValue || target.options?.find((option: KeyValuePair) => option.key === 'PCRFUsername')?.value;
    }

    target.options = target?.options?.[0]?.value ?? '';

    return target;
  };

  fieldUpdateHandler = (changedField: ChangedField): void => {
    const {
      targetType,
      targetIndex,
      onTargetUpdate,
      warrantConfigInstance,
      isTargetIdValidationEnabled,
      isExtendedTargetValidationEnabled,
    } = this.props;

    const {
      name, value,
    } = changedField;
    const { target, fields } = this.state;

    if (name === 'options') {
      target[name][0].value = value;

    } else {
      target[name] = value;
    }

    if (targetType === 'VZID' && changedField.name === 'secondaryType') {
      const newFields = cloneDeep(fields);

      if (changedField.value === '' || target.secondaryType === 'NONE') {
        newFields.secondaryValue.validation.required = false;
        newFields.secondaryValue.validation.validators = undefined;
        newFields.secondaryValue.disabled = true
        newFields.optionValue.disabled = true
      } else {
        newFields.secondaryValue.validation.validators = warrantConfigInstance.getTargetValidation(
          newFields,
          'secondaryValue',
          'regexp',
          changedField.value,
          'pattern',
        );
        newFields.secondaryValue.validation.required = true;
        if (target.secondaryType === 'TEL-URI') {
          newFields.optionValue.disabled = true
          newFields.secondaryValue.disabled = false
        } else {
          newFields.optionValue.disabled = false
          newFields.secondaryValue.disabled = false
        }
      }

      this.setState({ fields: newFields });
    }

    this.setState({ target });

    if (onTargetUpdate == null) {
      return;
    }

    if (isTargetIdValidationEnabled && !isExtendedTargetValidationEnabled) {
      if (this.isTargetConfirmationValidValid()) {
        onTargetUpdate(target, targetType, targetIndex);
      }
    } else {
      onTargetUpdate(target, targetType, targetIndex);
    }
  };

  fieldConfirmationUpdateHandler = (changedField: ChangedField): void => {
    const {
      name, value,
    } = changedField;
    const { targetConfirmation } = this.state;

    if (name === 'options') {
      targetConfirmation[name][0].value = value;

    } else {
      targetConfirmation[name] = value;
    }

    this.setState({ targetConfirmation });
  };

  static getFormTemplateFields = (
    formTemplateFields: FormTemplateFieldMap,
    mode: string,
    shouldBehaveAsPassword?: boolean,
    isConfirmField?: boolean,
    target?: any
  ): FormTemplateFieldMap => {

    if (mode === CONFIG_FORM_MODE.View) {
      setAllTemplateFieldsAsReadOnly(formTemplateFields);
    }

    if (shouldBehaveAsPassword) {
      Object.entries(formTemplateFields).forEach(([_fieldKey, fieldData]) => {
        fieldData.type = 'autoGrowPassword';

        fieldData.label = isConfirmField
          ? i18n.t('warrant.target.form.targetIdValidationLabel')
          : '';
      });
    }

    if (target?.primaryType === 'VZID') {
      if (target.secondaryType === '' || target.secondaryType === 'NONE') {
        formTemplateFields.secondaryValue.disabled = true
        formTemplateFields.optionValue.disabled = true
      } else if (target.secondaryType === 'TEL-URI') {
        formTemplateFields.optionValue.disabled = true
      }
    }

    return cloneDeep(formTemplateFields);
  };

  render(): JSX.Element {
    const { fields } = this.state;

    const {
      'data-testid': dataTestId, mode, isTargetIdValidationEnabled, isExtendedTargetValidationEnabled,
    } = this.props;
    const { target, targetConfirmation } = this.state;
    const shouldShowTargetValidation = isTargetIdValidationEnabled && !isExtendedTargetValidationEnabled;
    return (
      <td data-testid={dataTestId} className="devices-form-list-body-row-data">
        <DynamicForm
          ref={(ref: any) => { this.formRef = ref; }}
          name={`target_${new Date().getTime()}`}
          fields={TargetDetail.getFormTemplateFields(fields, mode, shouldShowTargetValidation, undefined, target)}
          layout={getSortedFormTemplateLayout(fields, TARGETS_DEFAULT_LAYOUT_ORDER)}
          defaults={this.customModifyTargetInputByType(target)}
          fieldUpdateHandler={this.fieldUpdateHandler}
          localizationDisabled
        />
        {shouldShowTargetValidation && (
          <div>
            <DynamicForm
              name={`target_${new Date().getTime() + 1}`}
              fields={TargetDetail.getFormTemplateFields(fields, mode, shouldShowTargetValidation, true)}
              layout={getSortedFormTemplateLayout(fields, TARGETS_DEFAULT_LAYOUT_ORDER)}
              defaults={this.customModifyTargetInputByType(targetConfirmation)}
              fieldUpdateHandler={this.fieldConfirmationUpdateHandler}
              localizationDisabled
            />
            {!this.isTargetConfirmationValidValid() && (
              <Label className="field-error-message">
                {i18n.t('warrant.target.form.targetIdValidationError')}
              </Label>
            )}
          </div>
        )}
      </td>
    );
  }
}
